/**
 * Typed provider orders API facade over the generated contract client.
 */

import { providerOrdersApi } from '../api/generated/providerOrders.api.js';
import {
  type CancelOrderRequest,
  type CancelOrderResponse,
  type ConfirmOrderRequest,
  type ConfirmOrderResponse,
  type CreateOrderRequest,
  type OrderCreateResponse,
  type OrderDetailResponse,
  type OrderDevicesResponse,
  type OrderInventoryAvailabilityResponse,
  type OrderListQuery,
  type OrderListResponse,
  type OrderUpdateResponse,
  type ProviderClaimOrderOnBehalfRequest,
  type ProviderClaimOrderOnBehalfResponse,
  type ShipOrderRequest,
  type ShipOrderResponse,
  type UpdateOrderRequest,
} from '@cnapse/shared/schemas.js';

class ProviderOrdersService {
  async listOrders(query?: OrderListQuery): Promise<OrderListResponse> {
    return providerOrdersApi.list(query ?? {}) as Promise<OrderListResponse>;
  }

  async getOrder(orderReference: string): Promise<OrderDetailResponse> {
    return providerOrdersApi.getByReference(orderReference) as Promise<OrderDetailResponse>;
  }

  async createOrder(data: CreateOrderRequest): Promise<OrderCreateResponse> {
    return providerOrdersApi.create(data) as Promise<OrderCreateResponse>;
  }

  async updateOrder(orderReference: string, data: UpdateOrderRequest): Promise<OrderUpdateResponse> {
    return providerOrdersApi.update(orderReference, data) as Promise<OrderUpdateResponse>;
  }

  async getOrderDevices(orderReference: string): Promise<OrderDevicesResponse> {
    return providerOrdersApi.getDevices(orderReference) as Promise<OrderDevicesResponse>;
  }

  async getOrderInventoryAvailability(
    orderReference: string,
  ): Promise<OrderInventoryAvailabilityResponse> {
    return providerOrdersApi.getInventoryAvailability(orderReference) as Promise<OrderInventoryAvailabilityResponse>;
  }

  async confirmOrder(
    orderReference: string,
    data: ConfirmOrderRequest = {},
  ): Promise<ConfirmOrderResponse> {
    return providerOrdersApi.confirm(orderReference, data) as Promise<ConfirmOrderResponse>;
  }

  async cancelOrder(orderReference: string, data: CancelOrderRequest): Promise<CancelOrderResponse> {
    return providerOrdersApi.cancel(orderReference, data) as Promise<CancelOrderResponse>;
  }

  async shipOrder(orderReference: string, data: ShipOrderRequest = {}): Promise<ShipOrderResponse> {
    return providerOrdersApi.ship(orderReference, data) as Promise<ShipOrderResponse>;
  }

  async claimOrderOnBehalf(
    orderReference: string,
    data: ProviderClaimOrderOnBehalfRequest,
  ): Promise<ProviderClaimOrderOnBehalfResponse> {
    return providerOrdersApi.claimOnBehalf(orderReference, data) as Promise<ProviderClaimOrderOnBehalfResponse>;
  }

  async listOrdersForTenant(tenantId: string): Promise<OrderListResponse> {
    return providerOrdersApi.listForTenant({ tenantId }) as Promise<OrderListResponse>;
  }
}

export const providerOrdersService = new ProviderOrdersService();
